/**
 * Filter Dependency Engine
 * --------------------------------------------------
 * Tek gerçek kaynak: Bir alanın görünürlük/zorunluluğunu, başka bir alanın
 * mevcut değerine göre hesaplar.
 *
 * Modern format (admin paneli yazıyor, DB'de var):
 *   { parent_key: 'property_type',
 *     show_when: ['Apartment', 'Villa'],   // or ['*'] (her zaman)
 *     option_mapping: { 'Apartment': ['1+0','1+1',...], 'Villa': [...] } }
 *
 * Legacy format (eski DB kayıtları for forward-compat):
 *   { parent_field_key: 'value' }  or  { parent_field_key: ['v1','v2'] }
 *
 * Boolean parent + show_when = ['true'] / ['false'] de desteklenir.
 */

/**
 * depends_on yapısını tek bir modern format'a normalize eder.
 * @param {object|null} dep
 * @returns {{parent_key: string, show_when: string[], option_mapping: Record<string,string[]>}|null}
 */
export function normalizeDependency(dep) {
  if (!dep || typeof dep !== 'object') return null;
  if (Object.keys(dep).length === 0) return null;
  if (dep.parent_key) {
    // Treat missing/empty show_when as wildcard ['*'] — a depends_on with only
    // a parent_key means "show whenever the parent has any value".
    let show_when = Array.isArray(dep.show_when) ? dep.show_when.map(String) : [];
    if (show_when.length === 0) show_when = ['*'];
    return {
      parent_key: dep.parent_key,
      show_when,
      option_mapping: dep.option_mapping && typeof dep.option_mapping === 'object'
        ? dep.option_mapping
        : {},
    };
  }
  // Legacy format
  const parent_key = Object.keys(dep)[0];
  const raw = dep[parent_key];
  const show_when = Array.isArray(raw)
    ? raw.map(String)
    : (raw === null || raw === undefined ? [] : [String(raw)]);
  return { parent_key, show_when, option_mapping: {} };
}

/**
 * Static map: property_type → property_class. Used to derive the class
 * when only the type is known (the listing form asks for property_type
 * directly, but many show_if rules gate by class). Matches the DB-level
 * option_mapping for the `emlak` schema (iter493).
 *
 * Iter494: This map can be hydrated at runtime by `loadPropertyClassification()`
 * to stay in lockstep with admin edits via `GET /form-schemas/property-classification`.
 * The static seeds below are the boot-time fallback.
 */
let PROPERTY_TYPE_TO_CLASS = {
  // Residential
  'Detached': 'Residential', 'Detached House': 'Residential',
  'Semi-Detached': 'Residential', 'Terraced': 'Residential',
  'End-of-Terrace': 'Residential', 'End-Terrace': 'Residential',
  'Bungalow': 'Residential', 'Flat': 'Residential',
  'Apartment': 'Residential', 'Maisonette': 'Residential',
  'Studio': 'Residential', 'Cottage': 'Residential', 'Mews': 'Residential',
  'Holiday Home': 'Residential', 'Whole Building': 'Residential',
  // Commercial
  'Office': 'Commercial', 'Retail Shop': 'Commercial', 'Shop': 'Commercial',
  'Restaurant / Café / Takeaway': 'Commercial',
  'Pub / Bar / Nightclub': 'Commercial',
  'Warehouse / Industrial Unit': 'Commercial',
  'Hotel / B&B / Guest House': 'Commercial',
  'Showroom / Garage / Workshop': 'Commercial',
  'Mixed Use / Investment': 'Commercial',
  'Block of Flats': 'Commercial',
  'Land for Commercial Use': 'Commercial',
  // Land
  'Plot': 'Land', 'Field': 'Land', 'Garden': 'Land',
  'Allotment': 'Land', 'Woodland': 'Land', 'Paddock': 'Land', 'Farm': 'Land',
};

let _classificationLoaded = false;

/**
 * Replace the FE static map with the canonical DB version. Should be called
 * once on app boot. Falls back to the static map on network / parse errors.
 * @param {Record<string,string>} typeToClass — `{Flat:'Residential', ...}`
 */
export function hydratePropertyClassification(typeToClass) {
  if (!typeToClass || typeof typeToClass !== 'object') return;
  if (Object.keys(typeToClass).length === 0) return;
  PROPERTY_TYPE_TO_CLASS = { ...PROPERTY_TYPE_TO_CLASS, ...typeToClass };
  _classificationLoaded = true;
}

/**
 * Lazy-load classification from backend if not already done. Safe to call
 * multiple times — only fires the network request once. Errors are
 * swallowed so the static fallback continues to work.
 */
export async function loadPropertyClassification(API) {
  if (_classificationLoaded || !API) return;
  _classificationLoaded = true; // guard against duplicate concurrent calls
  try {
    const axios = (await import('axios')).default;
    const res = await axios.get(`${API}/form-schemas/property-classification`);
    if (res.data?.type_to_class) {
      PROPERTY_TYPE_TO_CLASS = { ...PROPERTY_TYPE_TO_CLASS, ...res.data.type_to_class };
    }
  } catch (e) {
    // Backend not reachable yet — keep the static seed map. Reset the
    // flag so we can retry on the next call.
    _classificationLoaded = false;
  }
}

/** Test helper — read the current classification map. */
export function getPropertyClassification() {
  return { ...PROPERTY_TYPE_TO_CLASS };
}

function deriveValue(parentKey, values) {
  // Direct hit
  const direct = values?.[parentKey];
  if (direct !== undefined && direct !== null && direct !== '') return direct;
  // Derivation: property_class can be inferred from property_type. This
  // lets show_if rules that gate by property_class still work when the
  // listing form only asks for property_type.
  if (parentKey === 'property_class') {
    const t = values?.property_type;
    if (typeof t === 'string' && PROPERTY_TYPE_TO_CLASS[t]) {
      return PROPERTY_TYPE_TO_CLASS[t];
    }
  }
  return direct;
}

/**
 * Alan görünür mü?
 * @param {object} field — { key, depends_on, ... }
 * @param {Record<string, any>} values — formdaki güncel { fieldKey: value } map'i.
 * @returns {boolean} — bağımlılık tatmin oluyorsa true, yoksa gizle.
 */
export function isFieldVisible(field, values = {}) {
  // depends_on takes priority; fall back to legacy `show_if` when present.
  const rawDep = field?.depends_on || field?.show_if;
  const dep = normalizeDependency(rawDep);
  if (!dep) return true;

  const parentVal = deriveValue(dep.parent_key, values);
  // Wildcard: parent herhangi bir değere sahipse görünür
  if (dep.show_when.includes('*')) {
    return parentVal !== undefined && parentVal !== '' && parentVal !== null;
  }
  // Parent boolean → 'true'/'false' karşılaştırması
  if (typeof parentVal === 'boolean') {
    return dep.show_when.includes(String(parentVal));
  }
  // Multi-select / array değer
  if (Array.isArray(parentVal)) {
    return parentVal.some(v => dep.show_when.includes(String(v)));
  }
  // String / number
  if (parentVal === undefined || parentVal === null || parentVal === '') return false;
  return dep.show_when.includes(String(parentVal));
}

/**
 * Parent değere göre alt seçenek mapping'i (option_mapping). Yoksa field.options döner.
 * @returns {string[]}
 */
export function getMappedOptions(field, values = {}) {
  const dep = normalizeDependency(field?.depends_on || field?.show_if);
  if (!dep || !dep.option_mapping || Object.keys(dep.option_mapping).length === 0) {
    return field?.options || [];
  }
  const parentVal = deriveValue(dep.parent_key, values);
  if (parentVal === undefined || parentVal === null || parentVal === '') {
    return field?.options || [];
  }
  const key = String(parentVal);
  if (Array.isArray(dep.option_mapping[key]) && dep.option_mapping[key].length > 0) {
    return dep.option_mapping[key];
  }
  return field?.options || [];
}

/**
 * Birden fazla alanı görünürlüğe göre filtreler — tek pass.
 */
export function filterVisibleFields(fields = [], values = {}) {
  return fields.filter(f => isFieldVisible(f, values));
}
